import { useCustomQuery } from "@/hooks/useCustomQuery"
import DetailAvatar from "@/components/Avatar/DetailAvatar"
import { columns } from "@/pages/orders/DetailColumns"
import { CrudProvider } from "@/context/crudContext"

import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import { useState } from "react"
import { useParams, Link } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { apiFetch } from "@/hooks/apiFetch"
import { useAppSelector } from "@/lib/hooks"
import Loading from "@/components/Loading"
import { CustomTable } from "@/components/Tables/CustomTable"
import { withBranchColumn } from "@/components/Reports/ReportBranchFilter"

// A customer's sales span branches, so each row names its branch before the
// action column.
const orderColumns = [
  ...withBranchColumn(columns.slice(0, -1)),
  columns[columns.length - 1],
]

const CustomerDetails = () => {
  const { id } = useParams()

  const { data: response, isLoading } = useCustomQuery(
    ["customer-detail", id],
    `customer/${id}`
  )

  if (isLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  const customer = response?.data
  return (
    <div>
      <div className="breadcrumb-card">
        <h5>Customer Details</h5>

        <ul className="breadcrumb">
          <li>
            <Link to="/pos/dashboard">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>
            <Link to="/pos/customers">Customers</Link>
          </li>
          <li>{customer?.name}</li>
        </ul>
      </div>
      {customer ? (
        <>
          <CustomerSummary customer={customer} />
          <CrudProvider>
            <CustomTable
              label="Orders"
              columns={orderColumns}
              apiPath={`customer/${customer.id}/orders`}
              addBtn={false}
              action={false}
              perPage={10}
            />
          </CrudProvider>
        </>
      ) : (
        <div>No user data found.</div>
      )}
    </div>
  )
}

function CustomerSummary({ customer }) {
  const details = [
    ["Account ID", customer.id],
    ["Email", customer.email],
    ["Phone", customer.phone],
    ["Total Products", <TotalProducts key="total" customer={customer} />],
    ["Created By", customer.createdBy?.name],
    ["Updated By", customer.updatedBy?.name],
  ]

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      <Stack direction="row" alignItems="center" gap={2} mb={2}>
        <DetailAvatar name={customer.name} />
        <Typography variant="h6">{customer.name}</Typography>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        }}
      >
        {details.map(([label, value]) => (
          <Card key={label} variant="outlined" sx={{ boxShadow: "none" }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                {label}
              </Typography>
              <Typography
                component="div"
                sx={{ fontWeight: 500, overflowWrap: "anywhere", mt: 0.5 }}
              >
                {value ?? "—"}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Card>
  )
}

// Staff can correct the number of products a customer has bought. Purchases
// made afterwards keep counting up from the number they enter; clearing the
// field goes back to the counted total.
function TotalProducts({ customer }) {
  const canEdit = new Set(
    useAppSelector((state) => state.auth.user?.permissions ?? [])
  ).has("business-customers-update")
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState("")
  const [error, setError] = useState("")

  const save = useMutation({
    mutationFn: (totalProducts) =>
      apiFetch(`customer/${customer.id}/total-products`, {
        method: "PATCH",
        body: JSON.stringify({ total_products: totalProducts }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customer-detail", String(customer.id)],
      })
      setEditing(false)
      toast.success("Total products updated")
    },
    onError: (failure) => {
      setError(failure?.error?.total_products?.[0] ?? "Could not save")
    },
  })

  if (!editing) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <span>{customer.total_products ?? "—"}</span>
        {canEdit && (
          <IconButton
            size="small"
            aria-label="Edit total products"
            onClick={() => {
              setValue(String(customer.total_products ?? ""))
              setError("")
              setEditing(true)
            }}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
        )}
      </Stack>
    )
  }

  const submit = (event) => {
    event.preventDefault()
    const trimmed = value.trim()
    if (trimmed !== "" && !/^\d+$/.test(trimmed)) {
      setError("Enter a whole number of 0 or more")
      return
    }
    save.mutate(trimmed === "" ? null : Number(trimmed))
  }

  return (
    <Box component="form" onSubmit={submit} sx={{ mt: 0.5 }}>
      <TextField
        size="small"
        type="number"
        autoFocus
        fullWidth
        value={value}
        onChange={(event) => {
          setValue(event.target.value)
          setError("")
        }}
        inputProps={{ min: 0, step: 1 }}
        error={!!error}
        helperText={error || "Leave empty to use the counted total"}
      />
      <Stack direction="row" gap={1} mt={1}>
        <Button
          type="submit"
          size="small"
          variant="contained"
          disabled={save.isPending}
          sx={{ textTransform: "none" }}
        >
          {save.isPending ? "Saving..." : "Save"}
        </Button>
        <Button
          size="small"
          onClick={() => setEditing(false)}
          disabled={save.isPending}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
      </Stack>
    </Box>
  )
}

export default CustomerDetails
